import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nombreClinica } from './clinicas';
import { hhmm, hoyISO, useColeccion, useReloj } from './dia/comun';
import { dosisDe, tomasPendientes } from './hosp/logica';

// Avisos en tiempo real para todo el panel: llamadas nuevas, notas de turno nuevas y tomas de hospitalización.
// Salen como aviso en la esquina (con sonido) y como notificación del ordenador si se han activado.
const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 660].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.16);
      o.start(ctx.currentTime + i * 0.18); o.stop(ctx.currentTime + i * 0.18 + 0.17);
    });
  } catch { /* sin sonido */ }
};
const leer = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const guardarLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* nada */ } };

export default function Avisador({ clinicas, uid, esEquipo = false }) {
  useReloj(30000);
  const nav = useNavigate();
  const llamadas = useColeccion('llamadas', clinicas, [['hecha', '==', false]]);
  const notas = useColeccion('notasTurno', clinicas, [['dia', '>=', hoyISO(new Date(Date.now() - 2 * 864e5))]]);
  const hosp = useColeccion('hospitalizados', clinicas, [['activo', '==', true]]);
  const convs = useColeccion('conversaciones', clinicas, [['estado', '==', 'abierta']]);
  const [toasts, setToasts] = useState([]);
  const [permiso, setPermiso] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const [sonido, setSonido] = useState(leer('avisos-sonido', true));
  const [oculto, setOculto] = useState(leer('avisos-banner-oculto', false));
  const vistos = useRef(null);

  const avisar = (clave, titulo, texto, ruta) => {
    setToasts((t) => [...t.filter((x) => x.clave !== clave), { clave, titulo, texto, ruta, en: Date.now() }].slice(-2));
    if (sonido) beep();
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      try { const n = new Notification(titulo, { body: texto, tag: clave }); n.onclick = () => { window.focus(); nav(ruta); }; } catch { /* nada */ }
    }
  };

  useEffect(() => {
    if (!llamadas || !notas || !hosp || !convs) return;
    // WhatsApp: citas, urgencias, consultas para el vet o gente que pide hablar con alguien, sin atender.
    // Aviso al llegar y recordatorio cada 2 h (en horario de 10 a 20) mientras nadie conteste.
    const rutaWa = esEquipo ? '/' : '/whatsapp';
    const ms = (t) => (t?.toMillis ? t.toMillis() : t?.seconds ? t.seconds * 1000 : 0);
    const horaM = +new Intl.DateTimeFormat('es-ES', { hour: 'numeric', hour12: false, timeZone: 'Europe/Madrid' }).format(new Date());
    const avisosWa = convs.flatMap((c) => {
      const et = c.etiquetas || {};
      if (!(et.urgente || et.cita || et.veterinario || et.humano)) return [];
      const act = ms(c.actualizado);
      if (!act || ms(c.humanoHasta) - 4 * 36e5 >= act - 1000) return []; // ya contestó alguien del equipo
      const titulo = et.urgente ? 'Urgencia por WhatsApp' : et.cita ? 'Solicitud de cita' : et.veterinario ? 'Consulta para el veterinario' : 'Quieren hablar con alguien';
      const quien = c.solicitudCita?.nombre || c.nombre || `+${c.telefono}`;
      const texto = `${quien}${c.solicitudCita?.mascota ? ` (${c.solicitudCita.mascota})` : ''}: ${c.resumen || c.ultimoMensaje || ''}${clinicas.length > 1 ? ` · ${nombreClinica(c.clinica)}` : ''}`;
      const out = [{ clave: `wa-${c.id}-${act}`, titulo, texto, ruta: rutaWa }];
      const horas = (Date.now() - act) / 36e5;
      if (horas >= 2 && horaM >= 10 && horaM < 20) out.push({ clave: `wa-${c.id}-${act}-r${Math.floor(horas / 2)}`, titulo: `Sin contestar desde hace ${Math.floor(horas)} h`, texto, ruta: rutaWa });
      return out;
    });
    const claves = [
      ...avisosWa,
      ...llamadas.filter((l) => l.porUid !== uid).map((l) => ({ clave: `ll-${l.id}`, titulo: `Llamada para ${l.para || 'devolver'}`, texto: `${l.quien || ''}: ${l.motivo || ''}${clinicas.length > 1 ? ` · ${nombreClinica(l.clinica)}` : ''}`, ruta: '/dia' })),
      ...notas.filter((n) => n.porUid !== uid && !n.hecha && !(n.vistoPor || []).includes(uid)).map((n) => ({ clave: `no-${n.id}`, titulo: `${n.importante ? 'Nota importante' : 'Nota de turno'} de ${(n.por || '').split(' ')[0]}`, texto: n.texto, ruta: '/dia' })),
      ...hosp.flatMap((h) => tomasPendientes(h).map((p) => ({ clave: `to-${h.id}-${p.t.id}-${p.slot}-${p.estado}`, titulo: p.estado === 'atrasada' ? `Atrasado · ${h.mascota}` : p.t.cat === 'cuidado' ? `Toca · ${h.mascota}` : `Toca medicación · ${h.mascota}`, texto: `${p.t.farmaco} ${dosisDe(p.t, h.peso).txt} ${p.t.via} · ${hhmm(p.slot)}${h.jaula ? ` · ${h.jaula}` : ''}`, ruta: '/hospitalizacion' }))),
    ];
    // La primera vez solo se apunta lo que ya había (no se avisa de golpe de todo)
    if (vistos.current === null) { vistos.current = new Set(leer('avisos-vistos', [])); }
    const nuevos = claves.filter((c) => !vistos.current.has(c.clave));
    const primera = !leer('avisos-iniciado', false);
    nuevos.forEach((c) => { vistos.current.add(c.clave); if (!primera) avisar(c.clave, c.titulo, c.texto, c.ruta); });
    if (primera) guardarLS('avisos-iniciado', true);
    guardarLS('avisos-vistos', [...vistos.current].slice(-400));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llamadas, notas, hosp, convs, Math.floor(Date.now() / 60000)]);

  useEffect(() => { const i = setInterval(() => setToasts((t) => t.filter((x) => Date.now() - x.en < 60000)), 5000); return () => clearInterval(i); }, []);

  return (
    <>
      {permiso === 'default' && !oculto && import.meta.env.MODE !== 'demo' && (
        <div className="aviso-banner">
          Activa los avisos en este ordenador para enterarte de WhatsApp, llamadas, notas y medicación aunque estés en otra pestaña.
          <button className="btn mini" onClick={async () => setPermiso(await Notification.requestPermission())}>Activar</button>
          <button className="hz-link" onClick={() => { setOculto(true); guardarLS('avisos-banner-oculto', true); }}>ahora no</button>
        </div>
      )}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.clave} className="toast" onClick={() => { nav(t.ruta); setToasts((l) => l.filter((x) => x.clave !== t.clave)); }}>
            <b>{t.titulo}</b><div>{t.texto}</div>
            <button className="toast-x" onClick={(e) => { e.stopPropagation(); setToasts((l) => l.filter((x) => x.clave !== t.clave)); }}>✕</button>
          </div>
        ))}
        {toasts.length > 0 && <button className="toast-sonido" onClick={() => { setSonido(!sonido); guardarLS('avisos-sonido', !sonido); }}>{sonido ? 'Sonido activado' : 'Sin sonido'}</button>}
      </div>
    </>
  );
}
